import posthog from 'posthog-js'
import { isPosthogConfigured } from '@/shared/lib/posthog/posthogEnv'

/**
 * OBS-01/02 (Q28) — eventos de produto sem dado clínico. Cada evento só aceita as chaves da
 * sua allowlist; texto livre não passa (só rótulos curtos `a-z0-9_`, números e booleanos).
 * Nunca nome, texto, CID ou telefone.
 */
const PRODUCT_EVENT_KEYS = {
  document_saved: ['type', 'intent'],
  document_signed: ['type', 'surface'],
  document_shared: ['channel', 'signed'],
  dictation_used: ['screen', 'success', 'items'],
  intent_opened: ['kind', 'src', 'fields'],
} as const

export type ProductEventName = keyof typeof PRODUCT_EVENT_KEYS
type ProductEventValue = string | number | boolean
export type ProductEventProperties<E extends ProductEventName> = Partial<
  Record<(typeof PRODUCT_EVENT_KEYS)[E][number], ProductEventValue | null | undefined>
>

const SAFE_LABEL = /^[a-z0-9_]{1,40}$/

/** Mantém só chaves da allowlist do evento com valor seguro; o resto é descartado. */
export function filterProductEventProperties(
  event: ProductEventName,
  properties: Record<string, unknown>,
): Record<string, ProductEventValue> {
  const allowed: readonly string[] = PRODUCT_EVENT_KEYS[event] ?? []
  const out: Record<string, ProductEventValue> = {}
  for (const key of allowed) {
    const value = properties[key]
    if (typeof value === 'boolean') out[key] = value
    else if (typeof value === 'number' && Number.isFinite(value)) out[key] = value
    else if (typeof value === 'string' && SAFE_LABEL.test(value)) out[key] = value
  }
  return out
}

export function trackProductEvent<E extends ProductEventName>(event: E, properties: ProductEventProperties<E> = {}): void {
  if (!(event in PRODUCT_EVENT_KEYS) || !isPosthogConfigured()) return
  try {
    posthog.capture(`synap_${event}`, filterProductEventProperties(event, properties as Record<string, unknown>))
  } catch {
    // Medição nunca quebra o fluxo clínico.
  }
}
